import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import type { SelectableLanguageCode } from "@/models/i18n"
import { baseNumberColumnDef } from "@/nzds/data-grid"
import type { DecimalFormatOptions } from "@/utils/decimal"
import { formatUsage } from "@/utils/format-usage/format-usage"
import { getStringValue } from "@/utils/value"

import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro"

interface UseUsageColumnOptions {
  /** Keep the short label and skip units-system conversion. Defaults to converting. */
  convert?: boolean
  field?: string
  filterable?: boolean
  /** Fraction digits. Defaults to 0. Bill grids pass 2 and skip conversion. Interval records pass 2 and still convert. */
  precision?: number
  unitField?: string
}

export const useUsageColumn = <TRow extends GridValidRowModel>(
  options: UseUsageColumnOptions = {}
): GridColDef<TRow> => {
  const { i18n, t } = useTranslation("common")
  const { unitsSystem } = useUserSettings()
  const convert = options.convert !== false
  const field = options.field ?? "usage"
  const filterable = options.filterable ?? false
  const precision = options.precision ?? 0
  const unitField = options.unitField ?? "usage_unit"
  const formatOptions: DecimalFormatOptions = { precision }

  return {
    ...baseNumberColumnDef,
    field,
    filterable,
    flex: 1,
    headerName: t("fields.usage.title"),
    maxWidth: 220,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value, row) => {
      const rowRecord = row as Record<string, unknown>
      const usageUnit = getStringValue(rowRecord[unitField])

      return formatUsage(
        value as string | number | null | undefined,
        usageUnit,
        i18n.language as SelectableLanguageCode,
        unitsSystem,
        formatOptions,
        { convert }
      )
    },
  }
}
